import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTenantContext } from "../../context/TenantContext";
import { cn } from "../../lib/utils";
// EF-ONBOARDING-INVITATION-AND-BUILD-THEME-FIX-V1: Import build version
import { BUILD_VERSION, BUILD_INFO } from "../../lib/version";
// EF-SUPERADMIN-TENANT-SWITCH-V1: Import tenant components
import TenantSwitcher from "./TenantSwitcher";
import TenantContextBanner from "./TenantContextBanner";
import {
  LayoutDashboard,
  LayoutGrid,
  Users,
  MapPin,
  FolderKanban,
  Clock,
  ClipboardList,
  UserCheck,
  Calendar,
  Scale,
  FileText,
  Receipt,
  ChevronLeft,
  ChevronRight,
  Menu,
  Mail,
  Building2,
  Upload,
  Server,
  Sparkles,
  CalendarDays,
  Users2,
  Settings,
  User,
  Database,
  Trash2,
  Bug,
  TableProperties,
  Globe2,
  Contact,
  LogOut,
  Shield,
  ShieldCheck,
  ChevronDown,
  Info,
  Plug,
  Inbox,
  RefreshCw,
  ChevronDown as ChevronDownIcon,
  ChevronRight as ChevronRightIcon,
} from "lucide-react";
import { Button } from "../ui/button";
import { ThemeToggle } from "../theme/ThemeToggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import api from "../../lib/api";

// EVENTSFLO-ADMIN-UI-SIDEBAR-IA-CLEANUP-V1
// Sidebar information architecture restructured into named, collapsible
// groups. ALL existing routes and `superadminOnly` flags are preserved.
// Renderer:
//   - { type: "divider", label, groupKey, defaultCollapsed? } starts a
//     collapsible group. Click to expand/collapse. State is persisted
//     to localStorage under EF_SIDEBAR_GROUPS_KEY.
//   - { type: "subdivider", label } renders a small, non-collapsible
//     sub-header. Items after a subdivider until the next divider/
//     subdivider receive `indent` automatically.
//   - regular items render as a Link; items whose nearest parent group
//     is collapsed are hidden.
//   - The "Client Requests" pending badge / red dot logic is unchanged.
const EF_SIDEBAR_GROUPS_KEY = "ef:sidebar:groups:v1";

const navItems = [
  // ────────────────────────── MAIN ──────────────────────────
  { type: "divider", label: "Main", groupKey: "main", defaultCollapsed: false },
  { label: "Dashboard", icon: LayoutDashboard, path: "/" },
  { label: "Inbox", icon: Mail, path: "/inbox" },
  { label: "AI Onboard Crew", icon: Sparkles, path: "/ai-crew-onboarding", badge: "AI" },

  // ────────────────────────── INTAKE ──────────────────────────
  { type: "divider", label: "Intake", groupKey: "intake", defaultCollapsed: false },
  // EVENTSFLO-CLIENT-PORTAL-PHASE-2-PROJECT-SHIFT-REQUESTS-V1
  { label: "Client Requests", icon: Inbox, path: "/client-requests" },
  // EVENTSFLO-PUBLIC-QUOTE-AUTO-CLIENT-VENUE-MATCH-SUGGESTIONS-V1
  { label: "Public Quote Requests", icon: FileText, path: "/admin/public-quote-requests" },
  // EVENTSFLO-CLIENT-CHANGE-REQUESTS-ADMIN-APPLY-WORKFLOW-V1
  { label: "Change Requests", icon: RefreshCw, path: "/change-requests" },
  // EVENTSFLO-JOB-REQUEST-SOURCE-ARCHIVE-V1
  { label: "Job Request Archive", icon: FileText, path: "/admin/job-request-archive" },

  // ────────────────────────── OPERATIONS ──────────────────────────
  { type: "divider", label: "Operations", groupKey: "operations", defaultCollapsed: false },
  // Crew Planner promoted to the top of Operations.
  { label: "Crew Planner", icon: TableProperties, path: "/planner/sheet" },
  { label: "Projects", icon: FolderKanban, path: "/projects" },
  // Renamed from "Shifts" — same /shifts route, now positioned as a
  // cross-project secondary list.
  { label: "All Shifts (cross-project)", icon: Clock, path: "/shifts" },
  { label: "Assignment Board", icon: LayoutGrid, path: "/assignment-board" },
  // Renamed from "Assignments" — disambiguates from "My Assignments".
  { label: "All Assignments", icon: Calendar, path: "/assignments" },
  { type: "subdivider", label: "Calendars" },
  { label: "Project Shifts", icon: CalendarDays, path: "/calendar/projects" },
  { label: "Crew Assignments", icon: Users2, path: "/calendar/crew" },
  // "My Assignments" was previously under a misleading "Crew Portal"
  // header. Surface inside Operations — it is the admin's own upcoming
  // work, not the Crew Portal/PWA.
  { type: "subdivider", label: "My Work" },
  { label: "My Assignments", icon: ClipboardList, path: "/my-assignments" },

  // ────────────────────────── PEOPLE ──────────────────────────
  { type: "divider", label: "People", groupKey: "people", defaultCollapsed: false },
  { label: "Clients", icon: Users, path: "/clients" },
  // Renamed from "Crew Admin".
  { label: "Crew Management", icon: UserCheck, path: "/crew-management" },

  // ────────────────────────── RESOURCES ──────────────────────────
  { type: "divider", label: "Resources", groupKey: "resources", defaultCollapsed: false },
  { label: "Venues", icon: MapPin, path: "/venues" },
  { label: "Task Definitions", icon: ClipboardList, path: "/task-definitions" },
  { label: "Task Types", icon: FolderKanban, path: "/task-types" },
  // Previously routed but invisible — surfaced.
  { label: "Rules", icon: Scale, path: "/rules" },
  { type: "subdivider", label: "Task Definition Tools" },
  // Previously routed but invisible — surfaced.
  { label: "Bilingual Rename", icon: Globe2, path: "/settings/task-definitions/bilingual-renames" },
  { label: "Dependency Report", icon: FileText, path: "/settings/task-definitions/dependency-report" },

  // ────────────────────────── FINANCE ──────────────────────────
  { type: "divider", label: "Finance", groupKey: "finance", defaultCollapsed: false },
  { label: "Quotes", icon: FileText, path: "/quotes" },
  { label: "Invoices", icon: Receipt, path: "/invoices" },

  // EVENTSFLO-INTEGRITY-SUMMARY-MAIN-NAV-LINK-V1
  // Operator gate: run before and after entering real projects.
  { label: "Integrity", icon: ShieldCheck, path: "/integrity" },

  // ────────────────────────── SETTINGS ──────────────────────────
  { type: "divider", label: "Settings", groupKey: "settings", defaultCollapsed: false },
  // EF-SETTINGS-CONSOLIDATION-V1
  { label: "Settings Hub", icon: Settings, path: "/settings" },
  // EF-SETTINGS-CONSOLIDATION-V1: Renamed from "Company Settings" to "My Preferences"
  { label: "My Preferences", icon: User, path: "/settings/user" },
  // EF-SETTINGS-CONSOLIDATION-V1: Renamed from "Company Invoice Details" to "Company Profile"
  { label: "Company Profile", icon: Building2, path: "/settings/company" },
  { label: "Email Settings", icon: Mail, path: "/settings/email" },
  { label: "Email Templates", icon: FileText, path: "/settings/email-templates" },
  // Previously routed but invisible — surfaced.
  { label: "Portal Email Templates", icon: FileText, path: "/settings/portal-email-templates" },
  // EF-SETTINGS-CONSOLIDATION-V1 (PHASE 3)
  { label: "Integrations", icon: Plug, path: "/settings/company/integrations" },
  // Previously routed but invisible — surfaced.
  { label: "AI Shift Rules", icon: Sparkles, path: "/settings/ai-shift-rules" },
  { label: "System Contacts", icon: Contact, path: "/admin/system-contacts" },

  // ────────────────────────── ADMIN / MAINTENANCE ──────────────────────────
  { type: "divider", label: "Admin / Maintenance", groupKey: "maintenance", defaultCollapsed: true },
  { label: "Data Import", icon: Upload, path: "/settings/data-import" },
  { label: "Data Cleanup", icon: Sparkles, path: "/settings/data-cleanup" },
  // EVENTSFLO-SETTINGS-DELETE-EMERGENT-TEST-DATA-TOOL-V1
  { label: "Delete Test Data", icon: Trash2, path: "/settings/test-data-cleanup" },
  { label: "Debug / Test Tools", icon: Bug, path: "/settings/debug-tools" },
  { label: "Parser Aliases", icon: Globe2, path: "/settings/parser-aliases" },
  { label: "View Logs", icon: FileText, path: "/debug/logs" },
  { label: "System Info", icon: Server, path: "/settings/system-info" },

  // ────────────────────────── PLATFORM ADMIN (superadmin only) ──────────────────────────
  { type: "divider", label: "Platform Admin", groupKey: "platform-admin", defaultCollapsed: true, superadminOnly: true },
  { label: "Platform Settings", icon: Settings, path: "/settings/platform", superadminOnly: true },
  // EF-SUPERADMIN-TENANT-MGMT-V1
  { label: "Tenant Management", icon: Building2, path: "/admin/tenants", superadminOnly: true },
  // EF-RBAC-COMPLETION-AND-AUDIT-V1
  { label: "Roles & Permissions", icon: Shield, path: "/admin/roles", superadminOnly: true },
  // EVENTSFLO-ADMIN-BACKUP-SUPERADMIN-UI-V1
  { label: "Backups", icon: Database, path: "/admin/backups", superadminOnly: true },
];

export function DashboardLayout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [sessionInfo, setSessionInfo] = useState(null);
  // EVENTSFLO-CLIENT-REQUESTS-ADMIN-BADGE-AND-CONVERT-REVIEW-V1
  const [pendingClientRequests, setPendingClientRequests] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    let cancelled = false;
    const fetchPending = async () => {
      try {
        const r = await api.get("/admin/project-requests/pending-count");
        if (!cancelled) {
          setPendingClientRequests(Number(r.data?.count) || 0);
        }
      } catch {
        // Silent — non-admins (or stale tokens) shouldn't show the badge.
        if (!cancelled) setPendingClientRequests(0);
      }
    };
    fetchPending();
    // Refresh when we navigate away from the requests page (so converting
    // a request makes the badge decrement) or every 60s otherwise.
    const id = setInterval(fetchPending, 60000);
    // EVENTSFLO-CLIENT-REQUESTS-ADMIN-BADGE-AND-CONVERT-REVIEW-V1
    // Refetch immediately when a request is converted/rejected.
    const onRefresh = () => fetchPending();
    window.addEventListener(
      "eventsflo:client-requests-pending-count-refresh",
      onRefresh,
    );
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener(
        "eventsflo:client-requests-pending-count-refresh",
        onRefresh,
      );
    };
  }, [location.pathname]);

  // EF-SUPERADMIN-TENANT-MGMT-V1: Filter nav items based on user role.
  // EVENTSFLO-ADMIN-UI-SIDEBAR-IA-CLEANUP-V1: also hide entire group
  // dividers that are superadminOnly when the user is not a superadmin.
  const filteredNavItems = navItems.filter(item => {
    if (item.superadminOnly && user?.role !== 'superadmin') {
      return false;
    }
    return true;
  });

  // EVENTSFLO-ADMIN-UI-SIDEBAR-IA-CLEANUP-V1: Collapsible group state,
  // persisted to localStorage. Initial values come from each divider's
  // `defaultCollapsed` flag.
  const [openGroups, setOpenGroups] = useState(() => {
    const defaults = {};
    navItems.forEach((it) => {
      if (it.type === "divider" && it.groupKey) {
        defaults[it.groupKey] = !it.defaultCollapsed;
      }
    });
    try {
      const raw = typeof window !== "undefined"
        ? window.localStorage.getItem(EF_SIDEBAR_GROUPS_KEY)
        : null;
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          return { ...defaults, ...parsed };
        }
      }
    } catch {
      /* ignore corrupt persisted value */
    }
    return defaults;
  });

  const toggleGroup = (key) => {
    setOpenGroups((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try {
        window.localStorage.setItem(
          EF_SIDEBAR_GROUPS_KEY,
          JSON.stringify(next),
        );
      } catch {
        /* localStorage unavailable */
      }
      return next;
    });
  };

  // EF-USER-SESSION-UX-AND-BRANDING-V1: Load session info
  useEffect(() => {
    const loadSessionInfo = async () => {
      try {
        const response = await api.get("/auth/session");
        if (response.data) {
          setSessionInfo(response.data);
        }
      } catch (err) {
        console.error("Failed to load session info:", err);
      }
    };
    if (user) {
      loadSessionInfo();
    }
  }, [user]);

  // Load company profile for logo (tenant users only)
  useEffect(() => {
    const loadCompanyProfile = async () => {
      // EF-USER-SESSION-UX-AND-BRANDING-V1: Only load company profile for non-superadmin
      if (user?.role === 'superadmin') {
        setCompanyProfile(null);
        return;
      }
      try {
        const response = await api.get("/settings/company");
        if (response.data) {
          setCompanyProfile(response.data);
        }
      } catch (err) {
        // No profile configured, use default branding
      }
    };
    loadCompanyProfile();
  }, [user]);

  // EF-USER-SESSION-UX-AND-BRANDING-V1: Handle logout
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // EF-USER-SESSION-UX-AND-BRANDING-V1: Format role for display
  const formatRole = (role) => {
    switch (role) {
      case 'superadmin': return 'SuperAdmin';
      case 'tenant_admin': return 'Tenant Admin';
      default: return role?.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'User';
    }
  };

  // EF-USER-SESSION-UX-AND-BRANDING-V1: Format login time
  const formatLoginTime = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return null;
    }
  };

  // EF-USER-SESSION-UX-AND-BRANDING-V1 + EF-BRAND-LOGO-INTEGRATION-V1: Role-based branding
  const renderBranding = (showFull = true, inverted = false) => {
    // Superadmin always sees EventsFlo Platform branding with logo
    if (user?.role === 'superadmin') {
      return (
        <div className="flex items-center gap-2">
          <img 
            src="/eventsflo_logo.png" 
            alt="EventsFlo"
            className={cn("h-8 max-w-[140px] object-contain", inverted && "brightness-0 invert")}
            data-testid="eventsflo-logo-header"
          />
        </div>
      );
    }
    
    // Tenant users see their company logo if available
    if (companyProfile?.logo_url && showFull) {
      return (
        <img 
          src={companyProfile.logo_url} 
          alt={companyProfile.company_name || "Company Logo"}
          className={cn("h-6 max-w-[120px] object-contain", inverted && "brightness-0 invert")}
          data-testid="company-logo"
          onError={(e) => {
            e.target.style.display = "none";
            if (e.target.nextSibling) {
              e.target.nextSibling.style.display = "block";
            }
          }}
        />
      );
    }
    
    // EF-BRAND-LOGO-INTEGRATION-V1: Show EventsFlo logo for tenant admins without custom logo
    if (!companyProfile?.logo_url) {
      return (
        <div className="flex items-center gap-2">
          <img 
            src="/eventsflo_logo.png" 
            alt="EventsFlo"
            className={cn("h-7 max-w-[120px] object-contain", inverted && "brightness-0 invert")}
            data-testid="eventsflo-logo-header"
          />
        </div>
      );
    }
    
    // Fallback to company name or EventsFlo
    return (
      <span 
        className={cn(
          "font-secondary text-lg font-bold tracking-tight uppercase",
          inverted ? "text-white" : "text-primary"
        )}
        style={!inverted && companyProfile?.primary_color ? { color: companyProfile.primary_color } : {}}
      >
        {companyProfile?.company_name || sessionInfo?.company_name || "EventsFlo"}
      </span>
    );
  };

  // EF-USER-SESSION-UX-AND-BRANDING-V1: User info dropdown
  const renderUserInfo = (compact = false) => {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            className={cn(
              "gap-2 text-[hsl(var(--topbar-text))] hover:bg-white/10",
              compact ? "h-8 px-2" : "h-9 px-3"
            )}
            data-testid="user-menu-trigger"
          >
            <User className="h-4 w-4" />
            {!compact && (
              <>
                <span className="text-sm font-medium max-w-[150px] truncate">
                  {user?.email?.split('@')[0] || 'User'}
                </span>
                <ChevronDown className="h-3 w-3 opacity-50" />
              </>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col space-y-2">
              <p className="text-sm font-medium leading-none">{user?.email}</p>
              <div className="flex items-center gap-2">
                <span className={cn(
                  "text-xs px-2 py-0.5 rounded-full font-medium",
                  user?.role === 'superadmin' 
                    ? "bg-blue-500/20 text-blue-400" 
                    : "bg-green-500/20 text-green-400"
                )}>
                  {formatRole(user?.role)}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Tenant: {sessionInfo?.company_name || user?.company_id || 'Unknown'}
              </p>
              {sessionInfo?.login_time && (
                <p className="text-xs text-muted-foreground">
                  Login: {formatLoginTime(sessionInfo.login_time)}
                </p>
              )}
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem 
            onClick={handleLogout}
            className="text-red-400 focus:text-red-400 cursor-pointer"
            data-testid="logout-btn"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <div className="min-h-screen bg-surface-base">
      {/* Noise overlay */}
      <div className="noise-overlay" />

      {/* Mobile top bar - EF-USER-SESSION-UX-AND-BRANDING-V1: Added user menu */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 h-11 bg-[hsl(var(--topbar-bg))] border-b border-[hsl(var(--topbar-border))] px-3 flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-btn"
          className="h-8 w-8 p-0 text-[hsl(var(--topbar-text))] hover:bg-white/10"
        >
          <Menu className="h-4 w-4" />
        </Button>
        {renderBranding(true, true)}
        <div className="flex items-center gap-1">
          <ThemeToggle compact />
          {renderUserInfo(true)}
        </div>
      </div>

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 h-full z-40 transition-all duration-200 ease-out",
          "bg-card border-r border-border",
          collapsed ? "w-14" : "w-60",
          "hidden lg:block"
        )}
      >
        {/* Sidebar header - compact */}
        <div className="h-11 flex items-center justify-between px-3 border-b border-border">
          {!collapsed && renderBranding()}
          {!collapsed && companyProfile?.logo_url && (
            <span 
              className="font-secondary text-lg font-bold tracking-tight uppercase text-primary hidden"
              style={companyProfile?.primary_color ? { color: companyProfile.primary_color } : {}}
            >
              {companyProfile?.company_name || "EventsFlo"}
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCollapsed(!collapsed)}
            className="ml-auto h-7 w-7 p-0"
            data-testid="sidebar-toggle-btn"
          >
            {collapsed ? (
              <ChevronRight className="h-3.5 w-3.5" />
            ) : (
              <ChevronLeft className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>

        {/* Navigation - tighter spacing */}
        <nav className="p-1.5 space-y-0.5 overflow-y-auto h-[calc(100vh-2.75rem-2.5rem)]">
          {(() => {
            // EVENTSFLO-ADMIN-UI-SIDEBAR-IA-CLEANUP-V1
            // Walk the flat array and track the current group state so we
            // can hide items inside a collapsed group. Items after a
            // `subdivider` and before the next divider/subdivider get
            // automatically indented.
            let currentGroupKey = null;
            let currentGroupOpen = true;
            let indent = false;
            return filteredNavItems.map((item, idx) => {
              if (item.type === "divider") {
                currentGroupKey = item.groupKey || null;
                currentGroupOpen = currentGroupKey
                  ? openGroups[currentGroupKey] !== false
                  : true;
                indent = false;
                if (collapsed) {
                  // Collapsed sidebar: show a slim spacer instead of label.
                  return <div key={`d-${idx}`} className="pt-3" />;
                }
                if (!currentGroupKey) {
                  // Legacy plain divider — render as before.
                  return (
                    <div key={`d-${idx}`} className="pt-3 pb-1.5">
                      <span className="px-2.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        {item.label}
                      </span>
                    </div>
                  );
                }
                return (
                  <button
                    key={`d-${idx}`}
                    type="button"
                    onClick={() => toggleGroup(currentGroupKey)}
                    className="w-full flex items-center justify-between pt-3 pb-1.5 pl-2.5 pr-2 text-left hover:opacity-100 opacity-90"
                    data-testid={`sidebar-group-toggle-${currentGroupKey}`}
                    aria-expanded={currentGroupOpen}
                  >
                    <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                      {item.label}
                    </span>
                    {currentGroupOpen ? (
                      <ChevronDownIcon className="h-3 w-3 text-muted-foreground/70" strokeWidth={1.5} />
                    ) : (
                      <ChevronRightIcon className="h-3 w-3 text-muted-foreground/70" strokeWidth={1.5} />
                    )}
                  </button>
                );
              }

              if (item.type === "subdivider") {
                indent = true;
                if (!currentGroupOpen || collapsed) return null;
                return (
                  <div key={`sd-${idx}`} className="pl-3 pt-2 pb-0.5">
                    <span className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground/70">
                      {item.label}
                    </span>
                  </div>
                );
              }

              if (!currentGroupOpen) return null;

              const isActive = location.pathname === item.path;
              const Icon = item.icon;

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={cn(
                    "flex items-center gap-2.5 px-2.5 py-1.5 rounded-sm transition-colors duration-150 relative",
                    "hover:bg-surface-hover",
                    indent && !collapsed && "ml-3",
                    isActive && "bg-primary/10 text-primary border-l-2 border-primary -ml-px"
                  )}
                >
                  <Icon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                  {/* EVENTSFLO-CLIENT-REQUESTS-ADMIN-BADGE-AND-CONVERT-REVIEW-V1
                      Collapsed-sidebar red dot for Client Requests pending. */}
                  {collapsed && item.label === "Client Requests" && pendingClientRequests > 0 && (
                    <span
                      className="absolute top-1 right-1.5 h-2 w-2 rounded-full bg-red-600 ring-2 ring-card"
                      data-testid="sidebar-client-requests-dot"
                      aria-label={`${pendingClientRequests} pending client requests`}
                    />
                  )}
                  {!collapsed && (
                    <>
                      <span className="text-[13px] font-medium">{item.label}</span>
                      {/* EVENTSFLO-CLIENT-REQUESTS-ADMIN-BADGE-AND-CONVERT-REVIEW-V1 */}
                      {item.label === "Client Requests" && pendingClientRequests > 0 && (
                        <span
                          className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-600 text-white min-w-[18px] text-center"
                          data-testid="sidebar-client-requests-badge"
                        >
                          {pendingClientRequests > 99 ? "99+" : pendingClientRequests}
                        </span>
                      )}
                      {item.badge && (
                        <span className="ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded bg-primary/20 text-primary">
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                </Link>
              );
            });
          })()}
        </nav>
        
        {/* EF-SETTINGS-CONSOLIDATION-V1 (PHASE 3): Sidebar Footer with Version */}
        <div className="absolute bottom-0 left-0 right-0 h-10 border-t border-border bg-card px-3 flex items-center">
          {collapsed ? (
            <span className="text-[9px] font-mono text-muted-foreground/50 mx-auto" data-testid="sidebar-version-collapsed">
              {BUILD_VERSION.replace('v', '')}
            </span>
          ) : (
            <span className="text-[10px] font-mono text-muted-foreground/50" data-testid="sidebar-version">
              EventsFlo {BUILD_VERSION}
            </span>
          )}
        </div>
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-30 bg-black/50"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <aside
        className={cn(
          "lg:hidden fixed top-0 left-0 h-full z-40 w-60 transition-transform duration-200",
          "bg-card border-r border-border",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="h-11 flex items-center px-3 border-b border-border">
          {renderBranding()}
        </div>
        <nav className="p-1.5 space-y-0.5 overflow-y-auto h-[calc(100vh-2.75rem)]">
          {(() => {
            // EVENTSFLO-ADMIN-UI-SIDEBAR-IA-CLEANUP-V1: mirror of desktop
            // renderer so mobile reflects the same collapsible state.
            let currentGroupKey = null;
            let currentGroupOpen = true;
            let indent = false;
            return filteredNavItems.map((item, idx) => {
              if (item.type === "divider") {
                currentGroupKey = item.groupKey || null;
                currentGroupOpen = currentGroupKey
                  ? openGroups[currentGroupKey] !== false
                  : true;
                indent = false;
                if (!currentGroupKey) {
                  return (
                    <div key={`md-${idx}`} className="pt-3 pb-1.5">
                      <span className="px-2.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        {item.label}
                      </span>
                    </div>
                  );
                }
                return (
                  <button
                    key={`md-${idx}`}
                    type="button"
                    onClick={() => toggleGroup(currentGroupKey)}
                    className="w-full flex items-center justify-between pt-3 pb-1.5 pl-2.5 pr-2 text-left"
                    aria-expanded={currentGroupOpen}
                  >
                    <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                      {item.label}
                    </span>
                    {currentGroupOpen ? (
                      <ChevronDownIcon className="h-3 w-3 text-muted-foreground/70" strokeWidth={1.5} />
                    ) : (
                      <ChevronRightIcon className="h-3 w-3 text-muted-foreground/70" strokeWidth={1.5} />
                    )}
                  </button>
                );
              }

              if (item.type === "subdivider") {
                indent = true;
                if (!currentGroupOpen) return null;
                return (
                  <div key={`msd-${idx}`} className="pl-3 pt-2 pb-0.5">
                    <span className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground/70">
                      {item.label}
                    </span>
                  </div>
                );
              }

              if (!currentGroupOpen) return null;

              const isActive = location.pathname === item.path;
              const Icon = item.icon;

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className={cn(
                    "flex items-center gap-2.5 px-2.5 py-1.5 rounded-sm transition-colors duration-150",
                    "hover:bg-surface-hover",
                    indent && "ml-3",
                    isActive && "bg-primary/10 text-primary border-l-2 border-primary -ml-px"
                  )}
                >
                  <Icon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                  <span className="text-[13px] font-medium">{item.label}</span>
                </Link>
              );
            });
          })()}
        </nav>
      </aside>

      {/* Main content */}
      <main
        className={cn(
          "transition-all duration-200",
          collapsed ? "lg:ml-14" : "lg:ml-60",
          "pt-11 lg:pt-0"
        )}
      >
        {/* Desktop top bar - EF-USER-SESSION-UX-AND-BRANDING-V1: Added user info */}
        <div className="hidden lg:flex items-center justify-between h-11 px-4 border-b border-border bg-[hsl(var(--topbar-bg))]">
          {/* Left side - Session info */}
          <div className="flex items-center gap-4 text-xs text-[hsl(var(--topbar-text))]/70">
            {sessionInfo && (
              <>
                <span className="font-medium text-[hsl(var(--topbar-text))]">
                  {sessionInfo.email}
                </span>
                <span className={cn(
                  "px-2 py-0.5 rounded-full text-[10px] font-medium",
                  user?.role === 'superadmin' 
                    ? "bg-blue-500/20 text-blue-400" 
                    : "bg-green-500/20 text-green-400"
                )}>
                  {formatRole(user?.role)}
                </span>
                <span className="hidden xl:inline">
                  Tenant: {sessionInfo.company_name}
                </span>
                {sessionInfo.login_time && (
                  <span className="hidden 2xl:inline">
                    Login: {formatLoginTime(sessionInfo.login_time)}
                  </span>
                )}
              </>
            )}
          </div>
          {/* Right side - EF-SUPERADMIN-TENANT-SWITCH-V1: Added TenantSwitcher */}
          <div className="flex items-center gap-2">
            {/* EF-SUPERADMIN-TENANT-SWITCH-V1: Tenant Switcher for superadmins */}
            <TenantSwitcher />
            {/* EF-UI-STABILIZATION-AND-REGRESSION-FIX-V1: Build version display */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1 text-[10px] font-mono text-muted-foreground/50 cursor-default" data-testid="build-version-header">
                    <Info className="h-3 w-3" />
                    <span>{BUILD_VERSION}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p className="text-xs">Build: {BUILD_VERSION}</p>
                  <p className="text-xs text-muted-foreground">Date: {BUILD_INFO.date}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <ThemeToggle compact inverted />
            {renderUserInfo()}
          </div>
        </div>
        {/* EF-SUPERADMIN-TENANT-SWITCH-V1: Tenant Context Banner */}
        <TenantContextBanner />
        <div className="p-4 md:p-6 lg:p-5">
          {children}
        </div>
      </main>
    </div>
  );
}
